import type { DeliverOrderNumGroupItem, OrderNumGroupItem } from '@/api/order';
import { defineStore } from 'pinia';

import { getOrderNoticeList, getOrderNum, getReturnTj } from '@/api/order';
import { useAppStore } from '@/store/modules/app';
import { useUserStore } from '@/store/modules/user';

/**
 * 通知中心 Store
 *
 * 统一管理“我的”页面各类通知数量（徽标），包括：
 * - 订单通知：待支付 / 待发货 / 已发货 / 售后 等（getOrderNum.orderNumGroups）
 * - 货品状态：deliverOrderNumGroups 数组（“货品状态”区域展示）
 * - 其他菜单通知：优惠券 / 收藏 / 关注 / 足迹 / 退货 / 售后任务 / 退件寄回 / 缺货统计 等
 *
 * 字段命名与菜单配置 skins.badgeField 对齐，组件可通过 getBadge(field) 统一取值。
 */

/** 订单状态 → 菜单 badgeField 映射 */
const ORDER_STATUS_TO_FIELD: Record<number, string> = {
  1: 'waitPay', // 待支付
  2: 'waitDelivery', // 待发货
  3: 'shipped', // 已发货
  5: 'afterSale', // 全部退款（售后）
};

/** 货品状态项（与接口字段一致，driver为驼峰） */
export type DeliverNumItem = DeliverOrderNumGroupItem;

/** 订单数量字段（按菜单 badgeField 命名） */
export interface OrderNumMap {
  /** 待支付 */
  waitPay?: number
  /** 待发货 */
  waitDelivery?: number
  /** 已发货 */
  shipped?: number
  /** 售后（全部退款） */
  afterSale?: number
  [k: string]: number | undefined
}

/** 其他菜单通知数量字段（按 skins.badgeField 命名） */
export interface UserNoticeMap {
  /** 优惠券 */
  coupon?: number
  /** 商品收藏 */
  collectGoods?: number
  /** 店铺关注 */
  collectShop?: number
  /** 浏览足迹 */
  viewHistory?: number
  /** 缺货统计 */
  lanckinfo?: number
  /** 退货列表 */
  reflist?: number
  /** 售后任务 */
  saletask?: number
  /** 退件寄回 */
  exception?: number
  [k: string]: number | undefined
}

interface NoticeState {
  /** 订单数量（待支付/待发货/已发货/售后 等） */
  orderNum: OrderNumMap
  /** 货品状态统计列表 */
  deliverNum: DeliverNumItem[]
  /** 原始订单状态分组（如需原始数据饱和使用，例如订单列表 tab） */
  orderNumGroups: OrderNumGroupItem[]
  /** 其他菜单通知数量 */
  userNotice: UserNoticeMap
  /** 订单通知数量（消息中心用） */
  orderNoticeCount: number
}

const DEFAULT_ORDER_NUM: OrderNumMap = { waitPay: 0, waitDelivery: 0, shipped: 0, afterSale: 0 };

export const useNoticeStore = defineStore({
  id: 'notice',
  state: (): NoticeState => ({
    orderNum: { ...DEFAULT_ORDER_NUM },
    deliverNum: [],
    orderNumGroups: [],
    userNotice: {},
    orderNoticeCount: 0,
  }),
  getters: {
    /** 待支付数量 */
    waitPayCount(state): number {
      return Number(state.orderNum.waitPay || 0);
    },
    /** 待发货数量 */
    waitDeliveryCount(state): number {
      return Number(state.orderNum.waitDelivery || 0);
    },
    /** 已发货数量 */
    shippedCount(state): number {
      return Number(state.orderNum.shipped || 0);
    },
    /** 售后数量 */
    afterSaleCount(state): number {
      return Number(state.orderNum.afterSale || 0);
    },
    /** 是否存在货品状态数据 */
    hasDeliverData(state): boolean {
      return (state.deliverNum || []).length > 0;
    },
  },
  actions: {
    /**
     * 按字段名获取通知数量
     * 先从订单数量取值，再从其他通知取值
     */
    getBadge(field?: string): number {
      if (!field) return 0;
      const value = this.orderNum?.[field] ?? this.userNotice?.[field];
      return Number(value || 0);
    },

    /** 刷新订单数量 + 货品状态 */
    async refreshOrderNotice() {
      try {
        const res = await getOrderNum();
        const orderGroups = Array.isArray(res?.orderNumGroups) ? res.orderNumGroups : [];
        const deliverGroups = Array.isArray(res?.deliverOrderNumGroups) ? res.deliverOrderNumGroups : [];

        // 1. 原始订单状态分组（供订单列表如需使用 tab计数）
        this.orderNumGroups = orderGroups;

        // 2. 按 status 映射为 badgeField 可读名
        const map: OrderNumMap = { ...DEFAULT_ORDER_NUM };
        for (const item of orderGroups) {
          if (item.status == null) continue;
          const key = ORDER_STATUS_TO_FIELD[item.status];
          if (key) map[key] = Number(item.orderNum || 0);
        }
        this.orderNum = map;

        // 3. 货品状态（过滤 status 为 null 的“全部”项，避免重复展示）
        this.deliverNum = deliverGroups.filter(i => i.status != null);
      }
      catch {
        // 保持原值
      }
    },

    /**
     * 刷新其他菜单通知数量（优惠券/收藏/关注/退货/售后任务/退件寄回/缺货统计等）
     */
    async refreshUserNotice() {
      try {
        const data = (await getReturnTj()) || {};
        // 新接口字段 → 菜单 badgeField 映射（badgeField 保持不变）
        this.userNotice = {
          lanckinfo: Number(data.lackOrderDetailNum || 0),
          reflist: Number(data.unreadOrderRefundNum || 0),
          saletask: Number(data.unreadRefundTaskNum || 0),
          exception: Number(data.unreadReturnOrderNum || 0),
          quality: Number(data.unreadOrderQualityNum || 0),
        };
      }
      catch {
        // 保持原值
      }
    },

    /** 同时刷新所有通知（includeUser=false 时跳过售后统计 getReturnTj，仅刷订单数量） */
    async refreshAll(includeUser = true) {
      const tasks: Promise<any>[] = [this.refreshOrderNotice()];
      if (includeUser) {
        tasks.push(this.refreshUserNotice());
      }
      await Promise.all(tasks);
    },

    /** 刷新订单通知数量（消息中心-用户通知） */
    async refreshOrderNoticeCount() {
      try {
        const appStore = useAppStore();
        const sellerId = appStore.isSellerProject ? useUserStore().sellerId : undefined;
        const res = await getOrderNoticeList(sellerId);
        this.orderNoticeCount = res?.total || 0;
      }
      catch {
        // 保持原值
      }
    },

    /** 重置（登出/未登录场景） */
    reset() {
      this.orderNum = { ...DEFAULT_ORDER_NUM };
      this.deliverNum = [];
      this.orderNumGroups = [];
      this.userNotice = {};
      this.orderNoticeCount = 0;
    },
  },
});
